import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionsApi } from '@/Domain/api/Action/ApiAction/Actions.api';
import { ActionDto, AIActionUsageStatsDto } from '@/Domain/api/Action/dto/Action.dto';
import { ActionBox } from '@/Domain/components/Action/ActionBox/ActionBox';
import { RemoveActionModal } from '@/Domain/components/Action/RemoveAction.modal';
import { useUpdateActionDetails } from '@/Domain/hooks/Action/ApiAction/useUpdateActionDetails.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type Props = {
  action: ActionDto;
  isPreview?: boolean;
  stats?: AIActionUsageStatsDto;
};

export const ApiActionBox = ({ action, isPreview = false, stats }: Props) => {
  const { t } = useTranslation('actions_page');
  const queryClient = useQueryClient();
  const { toggleModal } = useModalStore();

  const { handleUpdateDetails } = useUpdateActionDetails();

  const handleFetchAction = async () =>
    await queryClient.fetchQuery({
      queryKey: [QueryKey.actions.httpRequest.get, action.id],
      queryFn: () => ActionsApi.get(action.id),
    });

  const handleActive = (isActive: boolean) =>
    handleUpdateDetails(action.id, { is_active: isActive }, true);

  const handleEdit = async () => {
    const httpRequestAction = await queryClient.ensureQueryData({
      queryKey: [QueryKey.actions.httpRequest.get, action.id],
    });
    toggleModal(MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET, { httpRequestAction });
  };

  const handleDelete = () => {
    toggleModal(`${MODAL_NAMES.REMOVE_ACTION_MODAL_NAME}_${action.id}`);
  };

  return (
    <>
      <ActionBox
        dataTestId="http-ai-action"
        isPreview={isPreview}
        action={action}
        stats={stats}
        onMouseEnterAction={handleFetchAction}
        onActive={handleActive}
        onEdit={handleEdit}
        onDelete={handleDelete}
        buttonLabel={t('button.edit_action')}
      />

      <RemoveActionModal actionId={action.id} />
    </>
  );
};
